import { useMemo, useState } from 'react';
import { useCrm } from '../store';
import { Card, PageTitle, Tag } from '../ui';
import { jLabel } from '../utils';
import { MapPin, Truck, Hourglass, CheckCircle2 } from 'lucide-react';

export default function Agenda() {
  const data = useCrm();
  const [year, setYear] = useState<string>('all');

  const signed = useMemo(() => data.prest
    .filter((p) => p.statut === 'Signé')
    .filter((p) => year === 'all' || p.dateEvent.startsWith(year))
    .sort((a, b) => a.dateEvent.localeCompare(b.dateEvent)),
    [data.prest, year]);

  const years = useMemo(() => Array.from(new Set(data.prest.map((p) => p.dateEvent.slice(0, 4)).filter(Boolean))).sort(), [data.prest]);

  return (
    <div>
      <PageTitle title="Agenda — Événements Signés" subtitle="Planning chronologique" />

      <div className="flex gap-1.5 mb-5">
        <button onClick={() => setYear('all')} className={`px-3 py-1.5 rounded-md text-xs border ${year === 'all' ? 'bg-sky-500/15 text-sky-400 border-sky-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800'}`}>Tout</button>
        {years.map((y) => (
          <button key={y} onClick={() => setYear(y)} className={`px-3 py-1.5 rounded-md text-xs border ${year === y ? 'bg-sky-500/15 text-sky-400 border-sky-500/40' : 'bg-slate-900/60 text-slate-400 border-slate-800'}`}>{y}</button>
        ))}
      </div>

      <div className="space-y-2">
        {signed.map((p) => {
          const d = new Date(p.dateEvent);
          return (
            <Card key={p.id} className="p-4 flex items-center gap-5 hover:border-slate-700 transition-colors">
              <div className="text-center min-w-[60px]">
                <div className="text-2xl font-heading text-amber-400 leading-none">{String(d.getDate()).padStart(2, '0')}</div>
                <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }).replace('.', '')}</div>
                <div className="text-[10px] text-amber-300 mt-1">{jLabel(p.dateEvent)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-200 font-medium">{p.client}</div>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
                  <MapPin size={11} strokeWidth={1.5} /> {p.lieu || '—'}
                  {p.km != null && <span className="inline-flex items-center gap-1 text-rose-400"><Truck size={10} strokeWidth={1.5} /> {p.km}km</span>}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <Tag color="emerald">{p.libelle}</Tag>
                  {p.services.slice(0, 4).map((s) => <Tag key={s}>{s}</Tag>)}
                  {p.artistes.slice(0, 2).map((a) => <Tag key={a} color="amber">{a}</Tag>)}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <CheckCircle2 size={18} className={p.gcalId ? 'text-emerald-400' : 'text-slate-600'} strokeWidth={1.5} />
                {p.gcalId ? (
                  <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                    <CheckCircle2 size={9} strokeWidth={1.5} /> Synchronisé{p.gcalId.startsWith('horaire') ? ' (horaire)' : ' (journée)'}
                  </span>
                ) : (
                  <span className="text-[10px] text-amber-400 flex items-center gap-1">
                    <Hourglass size={9} strokeWidth={1.5} /> Non sync
                  </span>
                )}
              </div>
            </Card>
          );
        })}
        {!signed.length && <Card className="p-12 text-center text-slate-500">Aucun événement signé pour cette période.</Card>}
      </div>
    </div>
  );
}
